import React from 'react';
import { useParams } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DocumentUpload from '../patientRegistration/DocumentUpload';
import { api } from '../../api';

function PatientDocuments() {
  const { patientCode, coupleCode } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [patientInfo, setPatientInfo] = React.useState(null);
  const [patientDocuments, setPatientDocuments] = React.useState([]);
  const [spouseDocuments, setSpouseDocuments] = React.useState([]);
  const [selectedDocType, setSelectedDocType] = React.useState('all');
  const [documentTypes, setDocumentTypes] = React.useState([]);
  const [documentTypesMap, setDocumentTypesMap] = React.useState({});
  const [hasSpouseInfo, setHasSpouseInfo] = React.useState(false);

  // Handle the case where coupleCode might be "undefined" string
  const actualCoupleCode = coupleCode === 'undefined' ? null : coupleCode;

  // Fetch document types on component mount
  React.useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const response = await api.contacts.getDocumentTypes();
        if (response?.data) {
          // Create a map of document type slug/id to name for easy lookup
          const typesMap = {};
          response.data.forEach(docType => {
            typesMap[docType.type_slug] = docType.type_name;
            typesMap[docType.id] = docType.type_name;
            // Also map the lowercase version for flexibility
            // typesMap[docType.name.toLowerCase()] = docType.name;
          });
          setDocumentTypesMap(typesMap);
          
          // For filtering dropdown, add 'all' at the beginning
          const typeNames = ['all', ...response.data.map(dt => dt.name)];
          setDocumentTypes(typeNames);
        }
      } catch (error) {
        console.error('Error loading document types:', error);
        // Fallback to default types if API fails
        setDocumentTypes(['all', 'Aadhaar', 'Passport', 'PAN', 'Visa', 'Driving License', 'Voter Card']);
      }
    };

    loadDocumentTypes();
  }, []);

  React.useEffect(() => {
    const loadPatientInfo = async () => {
      try {
        setLoading(true);
        
        // Fetch patient details including documents
        const response = await api.contacts.getPatientDocuments(patientCode);
        
        if (response?.data?.contact) {
          const patient = response.data.contact;
          const coupleInfo = response.data?.couple_info;
          
          // Process document data for better display
          const processDocuments = (docs = []) => {
            return docs.map(doc => {
              // Get the document type name from the map, with fallback formatting
              const getDocTypeName = (type, typeId) => {
                if (!type && !typeId) return 'Unknown';
                
                // Try to get from map using type ID first
                if (typeId && documentTypesMap[typeId]) {
                  return documentTypesMap[typeId];
                }
                
                // Try to get from map using type string
                if (type && documentTypesMap[type]) {
                  return documentTypesMap[type];
                }
                
                // Try lowercase version
                // if (type && documentTypesMap[type.toLowerCase()]) {
                //   return documentTypesMap[type.toLowerCase()];
                // }
                
                // // Fallback: Format the document type for better display (e.g., "aadhaar" -> "Aadhaar")
                // if (type) {
                //   // Handle hyphenated types like "driving-license"
                //   if (type.includes('-')) {
                //     return type.split('-')
                //       .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                //       .join(' ');
                //   }
                  
                //   // Handle single word types
                //   return type.charAt(0).toUpperCase() + type.slice(1);
                // }
                
                return 'Unknown';
              };
              
              return {
                ...doc,
                type: getDocTypeName(doc.documentType?.type_name, doc.documentType?.id),
                // Preserve the original documentType for better filtering in DocumentUpload
                documentType: doc.documentType,
                // Format dates if needed
                valid_till: doc.valid_till ? new Date(doc.valid_till).toLocaleDateString() : null
              };
            });
          };
          
          const patientDocs = processDocuments(response.data?.documents || []);
          const spouseDocs = processDocuments(response.data?.spouse?.documents || []);
          
          // Check if spouse information exists
          const spouseExists = response.data?.spouse?.contact?.id ? true : false;
          setHasSpouseInfo(spouseExists);
          
          setPatientInfo({
            patient_id: patient.id,
            external_code: patient.external_code,
            couple_id: coupleInfo?.id || null,
            couple_external_code: coupleInfo?.external_code || null,
            nationality: patient.nationality || 'Unknown',
            profile_picture: patient.profile_picture_path ? 
              `${patient.profile_picture_base_url}${patient.profile_picture_path}` : null,
            spouse_profile_picture: response.data?.spouse?.contact?.profile_picture_path ? 
              `${response.data.spouse.contact.profile_picture_base_url}${response.data.spouse.contact.profile_picture_path}` : null
          });
          
          setPatientDocuments(patientDocs);
          setSpouseDocuments(spouseDocs);

          // Set the required localStorage values for DocumentUpload component
          localStorage.setItem('patient_id', patient.id);
          localStorage.setItem('external_code', patient.external_code);
          localStorage.setItem('nationality', patient.nationality || 'Unknown');
          
          if (coupleInfo) {
            localStorage.setItem('couple_id', coupleInfo.id);
            localStorage.setItem('couple_external_code', coupleInfo.external_code);
          } else {
            localStorage.removeItem('couple_id');
            localStorage.removeItem('couple_external_code');
          }
          
          // Set spouse_id if spouse information is available
          if (response.data?.spouse?.contact?.id) {
            localStorage.setItem('spouse_id', response.data.spouse.contact.id);
          } else {
            localStorage.removeItem('spouse_id');
          }
        }
      } catch (error) {
        console.error('Error loading patient info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (patientCode) {
      loadPatientInfo();
    }
  }, [patientCode, actualCoupleCode, documentTypesMap]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox p={3}>
              <Typography>Loading patient information...</Typography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="logo"
            borderRadius="lg"
            coloredShadow="logo"
          >
            <MDTypography variant="h6" color="white">
              Patient Documents
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" mt={1}>
                Patient Code: {patientCode}
                {actualCoupleCode && ` | Couple Code: ${actualCoupleCode}`}
              </Typography>
              {patientInfo && (
                <Typography variant="body2" color="textSecondary" mt={0.5}>
                  Nationality: {patientInfo.nationality}
                </Typography>
              )}
            </Box>
            
            {/* Profile Pictures Section */}
            <Box mb={4}>
              <Typography variant="h6" mb={2}>Profile Pictures</Typography>
              <Box display="flex" gap={4} flexWrap="wrap">
                {/* Patient Profile Picture */}
                <Box>
                  <Typography variant="subtitle2" mb={1} sx={{ fontWeight: 'bold', color: '#00798E' }}>
                    Patient Profile Picture
                  </Typography>
                  {patientInfo?.profile_picture ? (
                    <Box
                      sx={{
                        width: 150,
                        height: 150,
                        border: '2px solid #00798E',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 4px 8px rgba(0, 121, 142, 0.3)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => {
                        window.open(patientInfo.profile_picture, '_blank');
                      }}
                    >
                      <img
                        src={patientInfo.profile_picture}
                        alt="Patient Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = `
                            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666;">
                              <span>No Image Available</span>
                            </div>
                          `;
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 150,
                        height: 150,
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        No Profile Picture
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Spouse Profile Picture */}
                {actualCoupleCode && hasSpouseInfo && (
                  <Box>
                    <Typography variant="subtitle2" mb={1} sx={{ fontWeight: 'bold', color: '#00798E' }}>
                      Spouse Profile Picture
                    </Typography>
                    {patientInfo?.spouse_profile_picture ? (
                      <Box
                        sx={{
                          width: 150,
                          height: 150,
                          border: '2px solid #00798E',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 4px 8px rgba(0, 121, 142, 0.3)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onClick={() => {
                          window.open(patientInfo.spouse_profile_picture, '_blank');
                        }}
                      >
                        <img
                          src={patientInfo.spouse_profile_picture}
                          alt="Spouse Profile"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = `
                              <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666;">
                                <span>No Image Available</span>
                              </div>
                            `;
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 150,
                          height: 150,
                          border: '2px dashed #ccc',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f9f9f9',
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          No Profile Picture
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            
            {/* Display Existing Documents */}
            <Box mb={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Patient Documents</Typography>
                
                {/* Document Type Filter */}
                <Box>
                  {/* <Typography variant="caption" display="inline" mr={1}>
                    Filter by type:
                  </Typography>
                  {documentTypes.map((type) => (
                    <Button
                      key={type}
                      size="small"
                      variant={selectedDocType === type ? "contained" : "text"}
                      color="primary"
                      sx={{ 
                        mx: 0.5, 
                        py: 0.5, 
                        minWidth: 'auto',
                        textTransform: 'capitalize'
                      }}
                      onClick={() => setSelectedDocType(type)}
                    >
                      {type}
                    </Button>
                  ))} */}
                </Box>
              </Box>

              {patientDocuments.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Type</TableCell>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Number</TableCell>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Issued By</TableCell>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Valid Till</TableCell>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Status</TableCell>
                        <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Actions</TableCell>
                      </TableRow>
                      {patientDocuments
                        .filter(doc => selectedDocType === 'all' || doc.type === selectedDocType)
                        .map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.type}</TableCell>
                            <TableCell>{doc.document_number}</TableCell>
                            <TableCell>{doc.issued_by || '-'}</TableCell>
                            <TableCell>{doc.valid_till || '-'}</TableCell>
                            <TableCell>
                              {doc.is_verified === 'Y' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'green' }}>
                                  <span style={{ marginRight: '4px' }}>✓</span> Verified
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'orange' }}>
                                  Pending Verification
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {doc.document_files && doc.document_files.length > 0 ? (
                                  doc.document_files.map((file, fileIndex) => (
                                    <Tooltip 
                                      key={fileIndex}
                                      title={file.sub_type ? file.sub_type.replace(/_/g, ' ').toUpperCase() : `File ${fileIndex + 1}`}
                                      arrow
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          // Open document preview in new tab
                                          const fileUrl = `${file.base_url}${file.file_path}`;
                                          window.open(fileUrl, '_blank');
                                        }}
                                        sx={{
                                          color: '#00798E',
                                          border: '1px solid #00798E',
                                          borderRadius: '4px',
                                          padding: '6px',
                                          '&:hover': {
                                            backgroundColor: 'rgba(0, 121, 142, 0.08)',
                                            transform: 'scale(1.05)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ))
                                ) : (
                                  <Typography variant="caption" color="textSecondary">
                                    No Files
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box py={2} px={2} bgcolor="#f5f5f5" borderRadius={1}>
                  <Typography variant="body2" color="textSecondary">
                    No documents available for this patient.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Display Spouse Documents if available */}
            {actualCoupleCode && hasSpouseInfo && (
              <Box mb={4}>
                <Typography variant="h6" mb={1}>Spouse Documents</Typography>
                {spouseDocuments.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Type</TableCell>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Number</TableCell>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Issued By</TableCell>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Valid Till</TableCell>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Status</TableCell>
                          <TableCell  sx={{ fontWeight: 'bold', color: '#00798E' }}>Actions</TableCell>
                        </TableRow>
                        {spouseDocuments.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.type}</TableCell>
                            <TableCell>{doc.document_number}</TableCell>
                            <TableCell>{doc.issued_by || '-'}</TableCell>
                            <TableCell>{doc.valid_till || '-'}</TableCell>
                            <TableCell>
                              {doc.is_verified === 'Y' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'green' }}>
                                  <span style={{ marginRight: '4px' }}>✓</span> Verified
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'orange' }}>
                                  Pending Verification
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {doc.document_files && doc.document_files.length > 0 ? (
                                  doc.document_files.map((file, fileIndex) => (
                                    <Tooltip 
                                      key={fileIndex}
                                      title={file.sub_type ? file.sub_type.replace(/_/g, ' ').toUpperCase() : `File ${fileIndex + 1}`}
                                      arrow
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          // Open document preview in new tab
                                          const fileUrl = `${file.base_url}${file.file_path}`;
                                          window.open(fileUrl, '_blank');
                                        }}
                                        sx={{
                                          color: '#00798E',
                                          border: '1px solid #00798E',
                                          borderRadius: '4px',
                                          padding: '6px',
                                          '&:hover': {
                                            backgroundColor: 'rgba(0, 121, 142, 0.08)',
                                            transform: 'scale(1.05)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ))
                                ) : (
                                  <Typography variant="caption" color="textSecondary">
                                    No Files
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box py={2} px={2} bgcolor="#f5f5f5" borderRadius={1}>
                    <Typography variant="body2" color="textSecondary">
                      No documents available for the spouse.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Document Upload Component */}
            {(() => {
              // Helper function to check if all document types exist for a person
              const hasAllDocuments = (documents) => {
                const existingTypeIds = documents.map(doc => doc.document_type?.id).filter(Boolean);
                const allTypeIds = [1, 2, 3, 4, 5, 6]; // All possible document type IDs
                return allTypeIds.every(typeId => existingTypeIds.includes(typeId));
              };

              const patientHasAllDocs = hasAllDocuments(patientDocuments);
              const spouseHasAllDocs = (actualCoupleCode && hasSpouseInfo) ? hasAllDocuments(spouseDocuments) : true;

              // Only show upload section if there are still documents to upload
              if (patientHasAllDocs && spouseHasAllDocs) {
                return (
                  <Box mt={4}>
                    <Typography variant="h6" mb={2}>Upload New Documents</Typography>
                    <Box py={3} px={2} bgcolor="#e8f5e8" borderRadius={1} border="1px solid #4caf50">
                      <Typography variant="body2" color="success.main" align="center">
                        ✓ All document types have been uploaded for this {(actualCoupleCode && hasSpouseInfo) ? 'couple' : 'patient'}.
                      </Typography>
                    </Box>
                  </Box>
                );
              }

              return (
                <Box mt={4}>
                  <Typography variant="h6" mb={2}>Upload New Documents</Typography>
                  <DocumentUpload 
                    label="Upload Patient Documents" 
                    existingPatientDocuments={patientDocuments}
                    existingSpouseDocuments={spouseDocuments}
                    existingPatientProfilePicture={patientInfo?.profile_picture}
                    existingSpouseProfilePicture={patientInfo?.spouse_profile_picture}
                    hasSpouseInfo={hasSpouseInfo}
                  />
                </Box>
              );
            })()}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PatientDocuments;
